// Tracks document uploads genuinely started in THIS browser session.
//
// An order carries `*Uploading` flags (clinicalDocumentationUploading,
// facesheetUploading) that drive the attachment-row spinner. They are purely a
// client-side view of an in-flight async upload — the backend never reads or
// writes them. They are set when an upload begins and cleared once the backend
// stamps the resulting file (or after a polling timeout).
//
// Two failure modes made rows spin forever, both fixed here:
//
//   1. The flags leaked into persisted orders. Several `Order - * - Update`
//      calls send the whole order object, so an update issued while an upload
//      was in flight (replacing a document, marking comments read) wrote
//      `clinicalDocumentationUploading: true` onto the stored vertex. The
//      backend passes the payload straight through, so every later fetch
//      returned the flag as true and the row spun on every open, forever.
//      `stripTransientUploadFlags` keeps them out of persistence and actively
//      overwrites any value already stored.
//
//   2. Healing was too narrow. It only cleared a flag when NO file had
//      arrived, so the common stuck case — a document that is present while
//      the flag says "uploading" — was never healed. The session set below is
//      the only trustworthy signal: a flag with no matching in-session upload
//      is stale regardless of whether a file exists (a replacement uploads
//      while the previous file is still attached).
const sessionUploads = new Set();

// Order field → upload type, for the flags that drive attachment spinners.
const UPLOAD_FLAGS = {
  clinicalDocumentationUploading: "clinicalDocumentation",
  facesheetUploading: "facesheet",
};

const key = (id, type) => `${id}:${type}`;

export const markUploadStarted = (id, type) => {
  if (id && type) sessionUploads.add(key(id, type));
};

// Release the mark once the upload resolves (extraction finished, or the poll
// gave up). Without this the mark outlives the upload and keeps suppressing
// the heal for the rest of the session.
export const markUploadFinished = (id, type) => {
  if (id && type) sessionUploads.delete(key(id, type));
};

export const isUploadActive = (id, type) => !!id && !!type && sessionUploads.has(key(id, type));

// Clear `*Uploading` flags that no in-session upload accounts for. Returns the
// original object untouched when there is nothing to heal, so callers can drop
// it straight into state without forcing a re-render.
export const healStaleUploadFlags = (order) => {
  if (!order) return order;
  let healed = order;
  for (const [flag, type] of Object.entries(UPLOAD_FLAGS)) {
    if (order[flag] && !isUploadActive(order.id, type)) {
      if (healed === order) healed = { ...order };
      healed[flag] = false;
    }
  }
  return healed;
};

// Produce a persistence-safe copy of an order. The flags are written back as
// `false` rather than deleted — dropping the keys would leave a previously
// persisted `true` untouched on a merging backend.
export const stripTransientUploadFlags = (order) => {
  if (!order) return order;
  const payload = { ...order };
  for (const flag of Object.keys(UPLOAD_FLAGS)) {
    payload[flag] = false;
  }
  return payload;
};
